<template>
  <q-table
    dense
    :class="$style.stickyHeader"
    :loading="loading"
    :filter="filterText"
    :rows-per-page-options="[0]"
    :rows="data"
    :columns="columns"
    row-key="index"
    style="height: calc(100vh - 50px - 68px)"
    flat
  >
    <template v-slot:top>
      <h6 class="q-ma-none">Korsande tågväg</h6>

      <q-space />

      <AppFieldDate
        :square="false"
        dense
        v-model="selectedDate"
        class="q-mr-md"
        hide-bottom-space
      />

      <q-input
        style="width: 100%; max-width: 300px"
        debounce="300"
        v-model="filterText"
        placeholder="Sök..."
        dense
        outlined
      >
        <template v-slot:append>
          <q-icon name="mdi-magnify" />
        </template>
      </q-input>

      <q-btn
        :loading="exporting"
        @click="onExport"
        class="q-ml-md"
        icon-right="mdi-file-export-outline"
        label="Export"
        color="grey-7"
        outline
      />
    </template>
  </q-table>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { QTable } from 'quasar'
import { useProfile } from '@/composable/useProfile'
import { exportExcel } from '@/services/export-excel'
import { getCrossingTrackDelays } from '@/api/crossing-track-delay/getCrossingTrackDelays'
import { CrossingTrackDelay } from '@/types/crossing-track-delay'
import { format } from 'date-fns'

import AppFieldDate from '@/components/AppFieldDate.vue'

export default defineComponent({
  name: 'VehicleDefectsTable',

  components: {
    AppFieldDate,
  },

  setup() {
    const data = ref<CrossingTrackDelay[]>([])
    const loading = ref(false)

    const selectedDate = ref(format(new Date(), 'yyyy-MM-dd'))

    const fetchAll = async () => {
      loading.value = true
      const res = await getCrossingTrackDelays(selectedDate.value, 'G')
      loading.value = false
      data.value = res.data
        ? res.data.map((x, i) => ({
            ...x,
            index: i,
          }))
        : []
    }

    watch(selectedDate, fetchAll, {
      immediate: true,
    })

    const { can } = useProfile()
    const exporting = ref(false)

    const columns: QTable['columns'] = [
      {
        align: 'left',
        name: 'otn',
        label: 'OTN',
        field: 'otn',
        sortable: true,
      },
      {
        align: 'left',
        name: 'explanations',
        label: 'Förklaring',
        field: 'explanations',
        format: (x) => x.join('\n'),
        sortable: true,
      },
    ]

    async function onExport() {
      exporting.value = true

      const exportData = {
        name: 'Korsande tågväg',
        sheets: [
          {
            name: 'Korsande tågväg',
            data: data.value.map((item) => {
              return columns?.reduce<{ [name: string]: unknown }>(
                (acc, col) => {
                  const value =
                    typeof col.field === 'function'
                      ? col.field(item)
                      : item[col.name as keyof CrossingTrackDelay]

                  if (col.format) {
                    acc[col.label] = col.format(value, null)
                  } else {
                    acc[col.label] = value
                  }

                  return acc
                },
                {}
              )
            }),
          },
        ],
      }

      exportExcel(exportData)
      exporting.value = false
    }

    return {
      columns,
      loading,
      data,
      exporting,
      can,
      onExport,
      filterText: ref(''),
      fetchAll,
      selectedDate,
    }
  },
})
</script>

<style lang="scss" module>
.stickyHeader {
  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th {
    background-color: white;
  }

  thead tr th {
    position: sticky;
    z-index: 1;
  }

  thead tr:first-child th {
    top: 0;
  }
}
</style>
